import { Button, Checkbox, Chip, Divider, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Paper, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service';
import { IReductionAction, IReductionActionForReportPeriod, ReductionActionType, Scope2Basis } from '../../../shared/services/reduction-action/reduction-action.model';
import { costPerTonnePerYear, formatCostPerTonne, formatTonnesRange, SCOPE2_BASIS_HELP, SCOPE2_BASIS_LABELS } from '../../../shared/utils/reduction-utils';
import { useSearchParams } from 'react-router-dom';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { DeleteOutlineOutlined } from '@mui/icons-material';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import ReportPeriodService from '../../../shared/services/report-period/report-period.service';
import { IReportPeriod } from '../../../shared/services/report-period/report-period.model';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { deleteQueryParamFromUrl } from '../../../shared/utils/misc-shared-utils';
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked';

export const ReductionActionPlanDialog: FunctionComponent<{
  actionType: ReductionActionType,
  open: boolean,
  closeHandler: () => void,
  getAllActionsForOrganisation: () => void,
  reductionActionsForOrganisation: IReductionAction[],
  submitHandler: (id: string, values: any) => void,
  deleteHandler: (reportPeriodId: string, reductionActionId: string) => void,
  isSubmitting: boolean,
}> = ({ actionType, open, closeHandler, getAllActionsForOrganisation, reductionActionsForOrganisation, submitHandler, deleteHandler, isSubmitting }) => {
  const [searchParams] = useSearchParams()
  const reductionActionService = new ReductionActionService()
  const userDetailsService = new UserDetailsService();
  const reportPeriodService = new ReportPeriodService();
  const userNotificationsContext = useContext(UserNotificationsContext)
  const isReportPeriodLocked = useIsReportPeriodLocked()
  const [reductionAction, setReductionAction] = useState<IReductionActionForReportPeriod>()
  const [openDate, setOpenDate] = useState(false);
  const [reportPeriodReductionActionId, setReportPeriodReductionActionId] = useState<string>()
  const [users, setUsers] = React.useState<IUserDetails[]>([]);
  const [reportPeriods, setReportPeriods] = React.useState<IReportPeriod[]>([]);

  const editActionSchema = Yup.object().shape({
    status: Yup.string().nullable(),
    dueDate: Yup.string().nullable(),
    userId: Yup.string().nullable(),
    annualReduction: Yup.number().min(0, actionType === 'flag_removal' ? 'Annual removal must be 0 or greater' : 'Annual reduction must be 0 or greater').nullable(),
    reductionStartReportingPeriodId: Yup.string().nullable(),
    reductionLifetimePeriods: Yup.number().integer('Must be a whole number').min(1, 'Must be at least 1 period').nullable(),
    reductionIsCumulative: Yup.boolean(),
    reductionIsPermanent: Yup.boolean(),
    scope2Basis: Yup.string().oneOf(['market', 'location', 'both']),
    capexGbp: Yup.number().integer('Whole pounds').min(0, 'Must be 0 or more').nullable(),
    annualOpexDeltaGbp: Yup.number().integer('Whole pounds').nullable(),
  })

  const formik = useFormik<any>({
    initialValues: {
      status: '',
      dueDate: '',
      userId: '',
      annualReduction: null,
      reductionStartReportingPeriodId: '',
      reductionLifetimePeriods: null,
      reductionIsCumulative: false,
      reductionIsPermanent: false,
      scope2Basis: 'both',
      capexGbp: null,
      annualOpexDeltaGbp: null,
    },
    validationSchema: editActionSchema,
    onSubmit: (values) => {
      if (reportPeriodReductionActionId) {
        const blankToNull = (value: any) => (value === null || value === '' || value === undefined ? null : Number(value))
        submitHandler(reportPeriodReductionActionId, {
          ...values,
          annualReduction:
            values.annualReduction === null || values.annualReduction === '' || values.annualReduction === undefined
              ? null
              : Number(values.annualReduction) * 1000,
          capexGbp: blankToNull(values.capexGbp),
          annualOpexDeltaGbp: blankToNull(values.annualOpexDeltaGbp),
        })
      }
    }
  });

  useEffect(() => {
    if (open && searchParams.has('actionId')) {
      const token = searchParams.get('actionId')
      if (token) {
        reductionActionService.getReportPeriodReductionAction(token, `actionType=${actionType}`)
          .then(response => {
            setReportPeriodReductionActionId(response.reportPeriodReductionAction.id)
            setReductionAction(response)
          })
          .catch(err => console.log(err))
      }
      deleteQueryParamFromUrl(searchParams, 'actionId')
    }
  }, [open, searchParams])

  useEffect(() => {
    if (reductionAction) {
      formik.setValues({
        status: reductionAction.reportPeriodReductionAction.inProgress ? 'In Progress' : reductionAction.reportPeriodReductionAction.isCompleted ? 'Completed' : 'Not Started',
        dueDate: reductionAction.reportPeriodReductionAction.dueDate ? moment(reductionAction.reportPeriodReductionAction.dueDate).format('DD/MM/YYYY') : '',
        userId: reductionAction.reportPeriodReductionAction.userId,
        annualReduction:
          reductionAction.reportPeriodReductionAction.annualReduction != null
            ? Number(reductionAction.reportPeriodReductionAction.annualReduction) / 1000
            : null,
        reductionStartReportingPeriodId: reductionAction.reportPeriodReductionAction.reductionStartReportingPeriodId || '',
        reductionLifetimePeriods: reductionAction.reportPeriodReductionAction.reductionLifetimePeriods,
        reductionIsCumulative: reductionAction.reportPeriodReductionAction.reductionIsCumulative ?? false,
        reductionIsPermanent: reductionAction.reportPeriodReductionAction.reductionIsPermanent ?? false,
        scope2Basis: reductionAction.reportPeriodReductionAction.scope2Basis ?? 'both',
        capexGbp: reductionAction.reportPeriodReductionAction.capexGbp ?? null,
        annualOpexDeltaGbp: reductionAction.reportPeriodReductionAction.annualOpexDeltaGbp ?? null,
      })
    }
  }, [reductionAction])

  useEffect(() => {
    userDetailsService.getAllUserDetails(`?status=active`)
      .then((response) => {
        setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
      })
      .catch(err => {
        console.log(err)
      })

    reportPeriodService.getAllReportPeriod()
      .then((response) => {
        const sorted = response.sort((a, b) => Number(a.calendarYear) - Number(b.calendarYear));
        setReportPeriods(sorted)
      })
      .catch(err => {
        console.log(err)
      })
  }, [])

  const dateHandler = (date: Moment | null) => {
    if (date) {
      const updatedReportPeriod = { ...formik.values, dueDate: date.format('YYYY-MM-DD') };
      formik.setValues(updatedReportPeriod);
    }
  }

  const deleteDialogHandler = () => {
    const locked =
      reductionAction?.reportPeriodReductionAction?.reportPeriod?.locked ?? isReportPeriodLocked === true
    if (locked) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The report period for this action is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this action? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response && reductionAction?.reportPeriodReductionAction.reportPeriodId) {
            deleteHandler(reductionAction.id, reductionAction.reportPeriodReductionAction.reportPeriodId)
          }
        })
        .catch(err => {
          console.log(err)
        })
    }
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={reductionAction?.name ?? 'Edit Action'}
      cancelHandler={closeHandler}
      submitHandler={formik.submitForm}
      isSubmitting={isSubmitting}
      disableSaveButton={
        reductionAction?.reportPeriodReductionAction?.reportPeriod?.locked ?? isReportPeriodLocked === true
      }
      disableCancelButton={false}
      maxWidth='sm'
    >
      {reductionAction && (
        <>
          <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
            {reductionAction.isCustom && <Chip label='Custom' size='small' />}
            {reductionAction.reportPeriodReductionAction.recommendationEvidence && <Chip label='Suggested from your data' size='small' variant='outlined' />}
          </Stack>
          {reductionAction.description && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>{reductionAction.description}</Typography>
          )}
          {reductionAction.reportPeriodReductionAction.recommendationEvidence && (
            <Paper variant='outlined' sx={{ backgroundColor: 'lightGrey.light', p: 2, mb: 2 }}>
              <Typography variant='body2'>{reductionAction.reportPeriodReductionAction.recommendationEvidence.reason}</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
                <b>What the figure assumes.</b> {reductionAction.reportPeriodReductionAction.recommendationEvidence.assumption}
              </Typography>
              {reductionAction.reportPeriodReductionAction.recommendationEvidence.saving && (
                <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
                  Suggested range {formatTonnesRange(reductionAction.reportPeriodReductionAction.recommendationEvidence.saving.minKg, reductionAction.reportPeriodReductionAction.recommendationEvidence.saving.maxKg)}CO<sub>2</sub>e a year. An estimate, not a measurement; the figure below is yours to change.
                </Typography>
              )}
              {reductionAction.reportPeriodReductionAction.recommendationEvidence.source?.reference && (
                <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>Source: {reductionAction.reportPeriodReductionAction.recommendationEvidence.source.reference}</Typography>
              )}
            </Paper>
          )}

          <Typography variant='subtitle2' sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>Delivery</Typography>

          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='userId'>Owner</InputLabel>
            <Select
              value={formik.values.userId || ''}
              onChange={(e: SelectChangeEvent<string>) => {
                formik.setFieldValue('userId', e.target.value);
              }}
              error={formik.touched.userId && Boolean(formik.errors.userId)}
              id='userId'
              name='userId'
              label='Owner'
            >
              {users.map((user, index) => (
                <MenuItem key={index} value={user.id}>{`${user.firstName} ${user.surname}`}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label='Due Date'
                format='DD/MM/YYYY'
                open={openDate}
                value={formik.values.dueDate ? moment(formik.values.dueDate, 'DD/MM/YYYY') : null}
                onChange={(date) => dateHandler(date)}
                onOpen={() => setOpenDate(true)}
                onClose={() => setOpenDate(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpenDate(true),
                  },
                }}
                minDate={moment()}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='status'>Status</InputLabel>
            <Select value={formik.values.status || ''} onChange={formik.handleChange} id='status' name='status' label='Status'>
              <MenuItem value='Not Started'>Not Started</MenuItem>
              <MenuItem value='In Progress'>In Progress</MenuItem>
              <MenuItem value='Completed'>Completed</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>Projection Impact</Typography>

          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='annualReduction'>{actionType === 'flag_removal' ? 'Annual Removal' : 'Annual Reduction'}</InputLabel>
            <OutlinedInput id='annualReduction' name='annualReduction'
              label={actionType === 'flag_removal' ? 'Annual Removal' : 'Annual Reduction'}
              type='number'
              value={formik.values.annualReduction ?? ''}
              onChange={formik.handleChange}
              endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
              error={formik.touched.annualReduction && Boolean(formik.errors.annualReduction)}
              inputProps={{ step: '0.01', min: 0 }}
            />
            {formik.touched.annualReduction && formik.errors.annualReduction && (
              <FormHelperText sx={{ color: '#bf3333' }}>{formik.errors.annualReduction as string}</FormHelperText>
            )}
          </FormControl>

          {actionType !== 'flag_removal' && reductionAction.group?.scope === '2' && (
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='scope2Basis'>Scope 2 basis</InputLabel>
              <Select
                value={formik.values.scope2Basis || 'both'}
                onChange={(e: SelectChangeEvent<string>) => formik.setFieldValue('scope2Basis', e.target.value as Scope2Basis)}
                id='scope2Basis'
                name='scope2Basis'
                label='Scope 2 basis'
              >
                {(Object.keys(SCOPE2_BASIS_LABELS) as Scope2Basis[]).map((basis) => (
                  <MenuItem key={basis} value={basis}>{SCOPE2_BASIS_LABELS[basis]}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{SCOPE2_BASIS_HELP[(formik.values.scope2Basis || 'both') as Scope2Basis]}</FormHelperText>
            </FormControl>
          )}

          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='reductionStartReportingPeriodId'>Start Reporting Period</InputLabel>
            <Select
              value={formik.values.reductionStartReportingPeriodId || ''}
              onChange={(e: SelectChangeEvent<string>) => {
                formik.setFieldValue('reductionStartReportingPeriodId', e.target.value || null);
              }}
              id='reductionStartReportingPeriodId'
              name='reductionStartReportingPeriodId'
              label='Start Reporting Period'
            >
              <MenuItem value=''>
                <Typography sx={{ color: 'text.secondary' }}>Select period</Typography>
              </MenuItem>
              {reportPeriods.map((period) => (
                <MenuItem key={period.id} value={period.id}>{period.calendarYear}</MenuItem>
              ))}
            </Select>
            <FormHelperText>Reporting period when this {actionType === 'flag_removal' ? 'removal' : 'reduction'} starts affecting projections</FormHelperText>
          </FormControl>

          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='reductionLifetimePeriods'>{actionType === 'flag_removal' ? 'Removal' : 'Reduction'} Lifetime (reporting periods)</InputLabel>
            <OutlinedInput id='reductionLifetimePeriods' name='reductionLifetimePeriods'
              label={`${actionType === 'flag_removal' ? 'Removal' : 'Reduction'} Lifetime (reporting periods)`}
              type='number'
              value={formik.values.reductionLifetimePeriods ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.reductionLifetimePeriods && Boolean(formik.errors.reductionLifetimePeriods)}
              inputProps={{ step: 1, min: 1 }}
              placeholder='Ongoing'
            />
            {formik.touched.reductionLifetimePeriods && formik.errors.reductionLifetimePeriods ? (
              <FormHelperText sx={{ color: '#bf3333' }}>{formik.errors.reductionLifetimePeriods as string}</FormHelperText>
            ) : (
              <FormHelperText>Number of reporting periods this {actionType === 'flag_removal' ? 'removal' : 'reduction'} is active. Leave blank if ongoing.</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.reductionIsCumulative}
                  onChange={(e) => formik.setFieldValue('reductionIsCumulative', e.target.checked)}
                />
              }
              label='Cumulative: stacks each year'
            />
            <FormHelperText sx={{ mt: -0.5 }}>
              Each active period adds another {formik.values.annualReduction ? `${formik.values.annualReduction} ` : ''}tCO<sub>2</sub>e
              on top of the last, instead of a flat annual saving.
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.reductionIsPermanent}
                  onChange={(e) => formik.setFieldValue('reductionIsPermanent', e.target.checked)}
                  disabled={formik.values.reductionLifetimePeriods == null || formik.values.reductionLifetimePeriods === ''}
                />
              }
              label='Permanent: does not revert'
            />
            <FormHelperText sx={{ mt: -0.5 }}>
              {formik.values.reductionLifetimePeriods == null || formik.values.reductionLifetimePeriods === ''
                ? `An ongoing ${actionType === 'flag_removal' ? 'removal' : 'reduction'} never reverts, so this only applies when a lifetime is set.`
                : 'The saving is kept after the lifetime ends, instead of reverting to previous levels.'}
            </FormHelperText>
          </FormControl>

          {actionType !== 'flag_removal' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>Cost (optional)</Typography>
              <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth variant='outlined' error={formik.touched.capexGbp && Boolean(formik.errors.capexGbp)}>
                  <InputLabel htmlFor='capexGbp'>Capital cost</InputLabel>
                  <OutlinedInput id='capexGbp' name='capexGbp' label='Capital cost' type='number'
                    value={formik.values.capexGbp ?? ''}
                    onChange={formik.handleChange}
                    startAdornment={<InputAdornment position='start'>£</InputAdornment>}
                    inputProps={{ step: 1, min: 0 }}
                  />
                  <FormHelperText>{formik.touched.capexGbp && formik.errors.capexGbp ? String(formik.errors.capexGbp) : 'One-off, whole pounds.'}</FormHelperText>
                </FormControl>
                <FormControl fullWidth variant='outlined' error={formik.touched.annualOpexDeltaGbp && Boolean(formik.errors.annualOpexDeltaGbp)}>
                  <InputLabel htmlFor='annualOpexDeltaGbp'>Running cost change</InputLabel>
                  <OutlinedInput id='annualOpexDeltaGbp' name='annualOpexDeltaGbp' label='Running cost change' type='number'
                    value={formik.values.annualOpexDeltaGbp ?? ''}
                    onChange={formik.handleChange}
                    startAdornment={<InputAdornment position='start'>£</InputAdornment>}
                    endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>a year</Typography></InputAdornment>}
                    inputProps={{ step: 1 }}
                  />
                  <FormHelperText>{formik.touched.annualOpexDeltaGbp && formik.errors.annualOpexDeltaGbp ? String(formik.errors.annualOpexDeltaGbp) : 'Negative for a saving on running costs.'}</FormHelperText>
                </FormControl>
              </Stack>
              {(() => {
                const perTonne = costPerTonnePerYear({
                  capexGbp: formik.values.capexGbp === '' || formik.values.capexGbp == null ? null : Number(formik.values.capexGbp),
                  annualOpexDeltaGbp: formik.values.annualOpexDeltaGbp === '' || formik.values.annualOpexDeltaGbp == null ? null : Number(formik.values.annualOpexDeltaGbp),
                  annualReductionKg: formik.values.annualReduction === '' || formik.values.annualReduction == null ? null : Number(formik.values.annualReduction) * 1000,
                  lifetimeYears: formik.values.reductionLifetimePeriods === '' || formik.values.reductionLifetimePeriods == null ? null : Number(formik.values.reductionLifetimePeriods),
                })
                return perTonne != null
                  ? <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1.5 }}>Cost per tonne: {formatCostPerTonne(perTonne)} (capital cost spread over the lifetime, or one year if ongoing).</Typography>
                  : null
              })()}
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Button
            variant='text'
            color='error'
            onClick={deleteDialogHandler}
            disabled={
              reductionAction?.reportPeriodReductionAction?.reportPeriod?.locked ?? isReportPeriodLocked === true
            }
            startIcon={<DeleteOutlineOutlined />}
            sx={{ textTransform: 'none' }}
          >
            Remove from Plan
          </Button>
        </>
      )}
    </DialogWrapper>
  )
}
